import React, { useEffect, useState } from "react";
import AdsNavbar from "./AdsNavbar";
import Calling from "../../api/Calling";
import { toast } from "react-toastify";
import { LuMegaphone, LuSave, LuKeyRound, LuSmartphone } from "react-icons/lu";

// The five Google ad-unit collections, in the order the backend merges them for the app (a later
// collection wins when two hold the same slot name).
const KINDS = {
  native: {
    label: "Native",
    get: Calling.getNativeAds,
    update: Calling.updateNativeAds,
    add: Calling.addNativeAds,
  },
  banner: {
    label: "Banner",
    get: Calling.getBannerAds,
    update: Calling.updateBannerAds,
    add: Calling.addBannerAds,
  },
  fullscreen: {
    label: "Full Screen",
    get: Calling.getFullScreenAds,
    update: Calling.updateFullScreenAds,
    add: Calling.addFullScreenAds,
  },
  open: {
    label: "Open",
    get: Calling.getOpenAds,
    update: Calling.updateOpenAds,
    add: Calling.addOpenAds,
  },
  reward: {
    label: "Reward",
    get: Calling.getRewardAds,
    update: Calling.updateRewardAds,
    add: Calling.addRewardAds,
  },
};

// One fixed ad-unit ID per Google Ads format: the slot named after the format.
const AD_UNITS = ["native", "fullscreen", "banner", "open", "reward"];

// The slots the Rewarded Play app already on users' phones (v2 / v3) reads by name, and where it
// uses each one.
const APP_SLOTS = [
  { name: "native_scratch", kind: "native", use: "Scratch screen" },
  { name: "native_captcha", kind: "native", use: "Captcha screen" },
  { name: "native_follow", kind: "native", use: "Follow Us screen" },
  { name: "native_profile", kind: "native", use: "Profile screen" },
  { name: "native_wallet", kind: "native", use: "Wallet screen" },
  { name: "native_home", kind: "native", use: "Saved by the app, not shown" },
  { name: "inter_scratchhome", kind: "fullscreen", use: "Home → Scratch" },
  { name: "inter_captchahome", kind: "fullscreen", use: "Home → Captcha" },
  { name: "inter_follow", kind: "fullscreen", use: "Home → Follow Us / Refer, and the Withdraw button" },
  { name: "inter_profile", kind: "fullscreen", use: "Home → Wallet" },
  { name: "inter_leader", kind: "fullscreen", use: "Home → Leaderboard" },
  { name: "inter_scratchreward", kind: "fullscreen", use: "Saved by the app, not shown" },
  { name: "inter_captchareward", kind: "fullscreen", use: "Saved by the app, not shown" },
  { name: "inter_hometowallet", kind: "fullscreen", use: "Saved by the app, not shown" },
  { name: "inter_refer", kind: "fullscreen", use: "Not read by the app" },
  { name: "reward_scratch", kind: "reward", use: "Scratch — empty gives the coins without an ad" },
  { name: "reward_captch", kind: "reward", use: "Captcha — empty gives the coins without an ad" },
  { name: "openad", kind: "open", use: "Saved by the app, not shown" },
];

const APP_SLOT_NAMES = new Set(APP_SLOTS.map((s) => s.name));

const SlotRow = ({ title, subtitle, tag, value, mono, allowEmpty, onSave }) => {
  const [text, setText] = useState(value || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setText(value || "");
  }, [value]);

  const handleSave = async () => {
    if (!text.trim() && !allowEmpty) {
      return toast.error(`Enter the ${title} ad unit ID`);
    }
    setSaving(true);
    try {
      await onSave(text.trim());
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 rounded-xl border border-main-border bg-white px-4 py-3 hover:border-brand-teal/40 hover:shadow-sm transition-all">
      {/* Icon + label + status */}
      <div className="flex items-center gap-3 sm:w-72 shrink-0 min-w-0">
        <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 text-white flex items-center justify-center shrink-0 shadow-sm shadow-teal-500/25">
          <LuKeyRound className="w-5 h-5" />
        </span>
        <div className="min-w-0">
          <p
            className={`font-semibold text-main-font leading-tight truncate ${
              mono ? "font-mono text-sm" : ""
            }`}
          >
            {title}
          </p>
          {subtitle && (
            <p className="text-[11px] text-main-subfont truncate" title={subtitle}>
              {subtitle}
            </p>
          )}
          <span className="inline-flex items-center gap-2 mt-0.5 text-[11px] font-medium">
            {value ? (
              <span className="inline-flex items-center gap-1.5 text-brand-teal">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-teal" />
                Configured
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-main-subfont">
                <span className="w-1.5 h-1.5 rounded-full bg-main-subfont/50" />
                Not set
              </span>
            )}
            {tag && (
              <span className="px-1.5 py-0.5 rounded-md bg-teal-500/10 text-brand-teal">
                {tag}
              </span>
            )}
          </span>
        </div>
      </div>

      {/* Input */}
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="ca-app-pub-xxxxxxxx/xxxxxxxx"
        className="ui-input flex-1 min-w-0 w-full px-4 py-3 rounded-lg font-mono text-sm"
      />

      {/* Save */}
      <button
        onClick={handleSave}
        disabled={saving}
        className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-teal-500 to-cyan-500 rounded-lg shadow-md shadow-teal-500/25 hover:brightness-110 disabled:opacity-60 transition shrink-0"
      >
        <LuSave className="w-4 h-4" /> {saving ? "Saving..." : "Save"}
      </button>
    </div>
  );
};

const Section = ({ icon, title, desc, children }) => (
  <div className="mt-6">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-9 h-9 rounded-xl bg-teal-500/15 text-brand-teal flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div>
        <h2 className="text-base sm:text-lg font-bold text-main-font leading-tight">
          {title}
        </h2>
        {desc && <p className="text-xs text-main-subfont mt-0.5">{desc}</p>}
      </div>
    </div>
    <div className="w-full space-y-3">{children}</div>
  </div>
);

const GoogleAds = () => {
  // Every ad document of the five collections: { kind, _id, a_name, a_id }
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const kinds = Object.keys(KINDS);
      const results = await Promise.all(kinds.map((k) => KINDS[k].get()));
      const all = [];
      results.forEach((resp, i) => {
        if (!resp?.status) return;
        for (const d of resp.data || []) {
          all.push({ kind: kinds[i], _id: d._id, a_name: d.a_name, a_id: d.a_id || "" });
        }
      });
      setDocs(all);
    } catch (error) {
      console.error(error);
      toast.error("Could not load the ad units");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  // A format's own unit: the slot named after the format, in that format's collection.
  const formatDoc = (kind) =>
    docs.find((d) => d.kind === kind && d.a_name === kind) || null;

  // A slot as the app receives it: by name, a later collection winning — the backend's merge.
  const slotDoc = (name) => {
    let found = null;
    for (const d of docs) if (d.a_name === name) found = d;
    return found;
  };

  const save = async (kind, existing, name, value) => {
    try {
      const resp = existing
        ? await KINDS[existing.kind].update(existing._id, { a_id: value })
        : await KINDS[kind].add({ a_name: name, a_id: value });
      if (resp?.status) {
        toast.success(`${name} saved`);
        load();
      } else {
        toast.error(resp?.message || "Failed to save");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    }
  };

  const others = docs.filter(
    (d) => !(d.a_name === d.kind && AD_UNITS.includes(d.kind)) && !APP_SLOT_NAMES.has(d.a_name)
  );

  return (
    <div className="text-main-font md:ml-56 px-4 sm:px-6 lg:px-8 mt-16 pb-12">
      {/* Airy header */}
      <div className="flex items-center gap-4 pt-6 pb-5">
        <div className="p-3.5 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30">
          <LuMegaphone className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-main-font">
            Ad Management
          </h1>
          <p className="text-sm text-main-subfont">
            Manage your in-app Google ad units
          </p>
        </div>
      </div>

      <AdsNavbar selectState={"nativeads"} />

      {loading ? (
        <p className="mt-6 text-sm text-main-subfont">Loading ad units…</p>
      ) : (
        <>
          <Section
            icon={<LuSmartphone className="text-lg" />}
            title="Installed app — ad slots"
            desc="The Rewarded Play app on users' phones (v2 / v3) reads these slots by name. An empty slot shows no Google ad there."
          >
            {APP_SLOTS.map((s) => {
              const doc = slotDoc(s.name);
              return (
                <SlotRow
                  key={s.name}
                  title={s.name}
                  subtitle={s.use}
                  tag={KINDS[doc?.kind || s.kind].label}
                  value={doc?.a_id || ""}
                  mono
                  allowEmpty={!!doc}
                  onSave={(v) => save(s.kind, doc, s.name, v)}
                />
              );
            })}
          </Section>

          <Section
            icon={<LuMegaphone className="text-lg" />}
            title="Google Ads — one unit per format"
            desc="For app versions built on this backend: one ad unit each for native, full screen, banner, open and reward."
          >
            {AD_UNITS.map((kind) => {
              const doc = formatDoc(kind);
              return (
                <SlotRow
                  key={kind}
                  title={`${KINDS[kind].label} Ad Unit`}
                  value={doc?.a_id || ""}
                  allowEmpty={false}
                  onSave={(v) => save(kind, doc, kind, v)}
                />
              );
            })}
          </Section>

          {others.length > 0 && (
            <Section
              icon={<LuKeyRound className="text-lg" />}
              title="Other slots"
              desc="Saved earlier under other names. The app does not read them."
            >
              {others.map((d) => (
                <SlotRow
                  key={`${d.kind}-${d._id}`}
                  title={d.a_name}
                  tag={KINDS[d.kind].label}
                  value={d.a_id}
                  mono
                  allowEmpty
                  onSave={(v) => save(d.kind, d, d.a_name, v)}
                />
              ))}
            </Section>
          )}
        </>
      )}
    </div>
  );
};

export default GoogleAds;
